import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../api/client";
import ScheduleComposer from "./ScheduleComposer";
import { TabState } from "./tabShared";
// Null-aware money: "—" when a figure is absent, "$0" for a real zero.
import { toMoney } from "../../Analytics/metricValue";
import "../../../styles/data-table.css";
// .ct-stack / .ct-actions / .ct-more come from customerTabs.css, loaded by tabShared.

/*
 * EventsBookingsTab — the events tenant's BOOKINGS tab on a customer card.
 *
 * Lists this customer's events on the shared gt-table (the same table the
 * Customers list uses): Date, Venue, Package, Status, Deposit / Balance,
 * Contract. Every cell renders "—" when the field is absent, so the tab is
 * correct before the data is fully wired.
 *
 * DATA SOURCE: /scheduling/events-calendar/ filtered to this customer. RT is
 * adding the customer filter param — this reads `CUSTOMER_PARAM`; set it to the
 * exact name RT ships. Until that param is live the tab renders its empty state
 * (the endpoint ignores an unknown param and returns the tenant-wide window),
 * which is why this tab is pushed now but deploys only once the param exists.
 */
const CUSTOMER_PARAM = "customer"; // TODO(RT): confirm the exact param name

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso + "T12:00:00");
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const STATUS_TONE = {
  confirmed: "ok",
  completed: "ok",
  scheduled: "",
  hold: "warn",
  canceled: "danger",
  cancelled: "danger",
  no_show: "warn",
};

export default function EventsBookingsTab({ record, onNewQuote }) {
  const navigate = useNavigate();
  const customerId =
    record?.raw?.id || record?.customer_id || record?.id || record?.raw?.customer_id || null;

  const [events, setEvents] = useState([]);
  const [state, setState] = useState("loading");
  const [booking, setBooking] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback(async () => {
    if (!customerId) {
      setState("ok");
      setEvents([]);
      return;
    }
    setState("loading");
    try {
      const { data } = await api.get("/scheduling/events-calendar/", {
        params: { [CUSTOMER_PARAM]: customerId },
      });
      setEvents(Array.isArray(data?.bookings) ? data.bookings : []);
      setState("ok");
    } catch {
      setState("error");
    }
  }, [customerId]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const rows = useMemo(
    () => [...events].sort((a, b) => String(a.date || "").localeCompare(String(b.date || ""))),
    [events]
  );

  const header = (
    <div className="ct-actions">
      <button type="button" className="ct-more" onClick={() => setBooking(true)}>
        + New Event
      </button>
      {onNewQuote ? (
        <button type="button" className="ct-more" onClick={onNewQuote}>
          + New Quote
        </button>
      ) : null}
    </div>
  );

  if (booking) {
    return (
      <div className="ct-stack">
        <ScheduleComposer
          customer={record}
          industry="events"
          onScheduled={() => {
            setReloadKey((k) => k + 1);
            setBooking(false);
          }}
          onCancel={() => setBooking(false)}
        />
      </div>
    );
  }

  if (state === "loading") {
    return <div className="ct-stack">{header}<TabState>Loading events…</TabState></div>;
  }
  if (state === "error") {
    return (
      <div className="ct-stack">
        {header}
        <TabState>Couldn't load events. Try again in a moment.</TabState>
      </div>
    );
  }

  return (
    <div className="ct-stack">
      {header}
      <div className="gt-table-scroll">
        <div className="gt-table-wrapper" style={{ minWidth: 820 }}>
          <table className="gt-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Venue</th>
                <th>Package</th>
                <th>Status</th>
                <th className="num">Deposit</th>
                <th className="num">Balance</th>
                <th>Contract</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr className="gt-row gt-row--static">
                  <td className="gt-cell" colSpan={7}>
                    <div className="gt-empty">No events for this customer yet. Click “+ New Event”.</div>
                  </td>
                </tr>
              ) : (
                rows.map((ev) => {
                  const tone = STATUS_TONE[ev.status] ?? "";
                  return (
                    <tr
                      key={ev.id}
                      className="gt-row"
                      onClick={() => ev.date && navigate(`/calendar/day/${ev.date}`)}
                    >
                      <td className="gt-cell">{fmtDate(ev.date)}</td>
                      <td className="gt-cell">{ev.venue_name || "—"}</td>
                      <td className="gt-cell cap">{ev.package || ev.event_type || ev.title || "—"}</td>
                      <td className="gt-cell">
                        <span className={`gt-pill ${tone}`}>{ev.status || "—"}</span>
                      </td>
                      <td className="gt-cell num">
                        {toMoney(ev.deposit_amount)}
                        {ev.deposit_paid ? <span style={{ color: "var(--color-success)", fontSize: 11 }}> paid</span> : null}
                      </td>
                      <td className="gt-cell num">
                        {toMoney(ev.balance_due)}
                        {ev.balance_paid ? <span style={{ color: "var(--color-success)", fontSize: 11 }}> paid</span> : null}
                      </td>
                      <td className="gt-cell">{ev.contract_status || "—"}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
